/**
 * React Query hook for Accounts (Global)
 * Provides automatic caching, background sync, and error handling
 * Can be used across all modules
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listAccount } from 'services/accountServices';
import { totalCountEvent } from 'services/generalServices';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches accounts data
 * Always loads the full list by getting total count first, then fetching all items
 * Same as Redux action implementation
 *
 * @param {number} pageLimit - Limit for fetching accounts
 * @param {string} pagination_token - Pagination token (default: '')
 * @param {string} pagination_direction - Pagination direction ('forward' or 'reverse') (default: 'forward')
 * @param {string} next - Next page token (default: '')
 * @param {Object} payloadData - Payload data for POST request (default: undefined)
 * @param {boolean} excludeFromLoading - Exclude from loading state (default: false)
 * @returns {Promise<Array>} - Array of accounts
 */
const fetchAccounts = async (
  pageLimit,
  pagination_token = '',
  pagination_direction = 'forward',
  next = '',
  payloadData = undefined,
  excludeFromLoading = false
) => {
  try {
    // Fetch accounts with all parameters
    const response = await listAccount(pageLimit, pagination_token, pagination_direction, next, payloadData, excludeFromLoading);

    return response?.data?.account || [];
  } catch (error) {
    console.error('Error fetching accounts:', error);
    throw error;
  }
};

/**
 * Hook to fetch accounts with React Query
 * Always loads the full list by getting total count first (same as Redux action implementation)
 *
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Number of items per page (default: uses total count)
 * @param {string} options.pagination_token - Pagination token (default: '')
 * @param {string} options.pagination_direction - Pagination direction (default: 'forward')
 * @param {string} options.next - Next page token (default: '')
 * @param {Object} options.payloadData - Payload data for POST request (default: undefined)
 * @param {string} [options.accountType] - Sent as `account_type` in the POST body (e.g. 'operating', 'trust'); merged with payloadData
 * @param {boolean} options.excludeFromLoading - Exclude from loading state (default: false)
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} - accountList, isAccountLoading, error, refetch, isFetching
 */
export const useListAccountsQuery = ({
  limit,
  pagination_token = '',
  pagination_direction = 'forward',
  next = '',
  payloadData = undefined,
  accountType = '',
  excludeFromLoading = false,
  enabled = true,
} = {}) => {
  const resolvedPayloadData = useMemo(() => {
    const fromPayload = payloadData && typeof payloadData === 'object' ? { ...payloadData } : {};
    if (accountType) {
      return { ...fromPayload, account_type: accountType };
    }
    return Object.keys(fromPayload).length ? fromPayload : undefined;
  }, [payloadData, accountType]);

  // Get total count first (same as Redux action) - only if limit is not provided
  const { data: countData } = useQuery({
    queryKey: ['account-count'],
    queryFn: async () => {
      const countResponse = await totalCountEvent('account', '', '', '', '', excludeFromLoading);
      return countResponse?.data || 10;
    },
    enabled: enabled && !limit, // Only fetch count if limit is not provided
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    retry: 2, // Retry twice on failure
  });

  const pageLimit = limit || countData || 10;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.accounts.list(pageLimit, pagination_token, pagination_direction, next, resolvedPayloadData),
    queryFn: () => fetchAccounts(pageLimit, pagination_token, pagination_direction, next, resolvedPayloadData, excludeFromLoading),
    enabled: enabled && (limit || !!countData), // Only fetch when limit is provided or count is available
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    retry: 2, // Retry twice on failure
  });

  return {
    accountList: data || [],
    isAccountLoading: isLoading,
    isFetching,
    error,
    refetch,
  };
};
